// src/components/post/CommentList.jsx
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../context/userContext';

import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import Loader from '../common/Loader';

const CommentList = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [repliesMap, setRepliesMap] = useState({});
  
  const { currentUser } = useContext(UserContext);

  // Fetch comments on component mount
  useEffect(() => {
    fetchComments();
  }, [postId]);

  // Fetch all comments for a post
  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/comments/post/${postId}`);
      setComments(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  // Handle new comment submission
  const handleCommentAdded = (newComment) => {
    setComments(prev => [newComment, ...prev]);
  };

  // Handle reply to a comment
  const handleReplyAdded = async (commentId, newReply) => {
    try {
      // If we don't have replies for this comment yet, fetch them
      if (!repliesMap[commentId]) {
        await fetchReplies(commentId);
      } else {
        // Otherwise just add the new reply to the existing ones
        setRepliesMap(prev => ({
          ...prev,
          [commentId]: [...(prev[commentId] || []), newReply]
        }));
      }
    } catch (error) {
      console.error('Error handling reply:', error);
    }
  };

  // Fetch replies for a specific comment
  const fetchReplies = async (commentId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/comments/${commentId}/replies`);
      setRepliesMap(prev => ({
        ...prev,
        [commentId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  // Toggle showing replies for a comment
  const toggleReplies = async (commentId) => {
    if (repliesMap[commentId]) {
      // If already loaded, just toggle visibility
      setRepliesMap(prev => ({
        ...prev,
        [commentId]: prev[commentId] ? null : []
      }));
    } else {
      // If not loaded yet, fetch them
      await fetchReplies(commentId);
    }
  };

  // Update a comment in the state
  const handleCommentUpdated = (updatedComment) => {
    setComments(prev => 
      prev.map(comment => 
        comment._id === updatedComment._id ? updatedComment : comment
      )
    );
  };

  // Delete a comment from the state
  const handleCommentDeleted = (commentId) => {
    setComments(prev => prev.filter(comment => comment._id !== commentId));
  };

  if (loading) {
    return <div className="py-8"><Loader /></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red p-4 rounded-md my-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-8 px-4 sm:px-6 rounded-lg">
      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
        <span>Comments</span>
        <span className="ml-2 text-sm font-normal bg-gray-200 px-2 py-0.5 rounded-full">
          {comments.length}
        </span>
      </h3>
      
      <div className="mb-8">
        <CommentForm postId={postId} onCommentAdded={handleCommentAdded} />
      </div>
      
      {comments.length === 0 ? (
        <div className="bg-white p-8 text-center rounded-lg shadow-sm">
          <p className="text-gray-500">Be the first to comment on this post!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {comments.map(comment => (
            <div key={comment._id} className="comment-container">
              <CommentItem 
                comment={comment} 
                postId={postId}
                onReplyAdded={(newReply) => handleReplyAdded(comment._id, newReply)}
                onCommentUpdated={handleCommentUpdated}
                onCommentDeleted={handleCommentDeleted}
              />
              
              {comment.replyCount > 0 && (
                <button 
                  className="ml-14 text-sm text-primary hover:text-primary-dark transition-colors flex items-center gap-1 mb-3"
                  onClick={() => toggleReplies(comment._id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    {repliesMap[comment._id] 
                      ? <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      : <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    }
                  </svg>
                  {repliesMap[comment._id] 
                    ? `Hide ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}` 
                    : `View ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
                </button>
              )}
              
              {repliesMap[comment._id] && (
                <div className="ml-10 pl-4 border-l-2 border-gray-200">
                  {repliesMap[comment._id].map(reply => (
                    <CommentItem 
                      key={reply._id}
                      comment={reply} 
                      postId={postId}
                      onCommentUpdated={handleCommentUpdated}
                      onCommentDeleted={handleCommentDeleted}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentList;